import { useState, useEffect, useCallback, useRef } from 'react';
import TopNav from './TopNav';
import { useToast } from '../Toast';
import { LocationLanding, InventoryTab, isoWeekStart, LOCATIONS } from './SpiritsOrdering';

const API = process.env.REACT_APP_API_URL || '';

// Bartender-facing spirits inventory intake. Reuses the count form + endpoints
// from the Spirits Ordering tool, minus pars/order/send — staff just list what's
// on the shelf (Display Shelves + Storage) per item, per location, for the week.
// Managers turn this into an order in the separate Spirits Ordering tool.
export default function SpiritsInventory({ user, onBack, focus }) {
  const toast = useToast();
  const [location, setLocation] = useState(null);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const weekStart = isoWeekStart();

  const loadSettings = useCallback(async () => {
    const r = await fetch(`${API}/api/spirits/settings`, { credentials: 'include' });
    if (r.ok) setSettings(await r.json());
  }, []);

  const loadItems = useCallback(async () => {
    const r = await fetch(`${API}/api/spirits/items`, { credentials: 'include' });
    if (r.ok) setItems(await r.json());
  }, []);

  const loadCounts = useCallback(async () => {
    if (!location) return;
    const r = await fetch(`${API}/api/spirits/counts?location=${location}&week_start=${weekStart}`, { credentials: 'include' });
    if (r.ok) { const d = await r.json(); setCounts(d.counts || []); }
  }, [location, weekStart]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Deep-link: /spirits-inventory?location=midtown jumps straight into that
  // location (week is always the current ISO week here, so any week param is ignored).
  const didFocus = useRef(false);
  useEffect(() => {
    if (didFocus.current || !focus?.location) return;
    if (!LOCATIONS.some((l) => l.key === focus.location)) return;
    didFocus.current = true;
    setLocation(focus.location);
  }, [focus]);

  useEffect(() => {
    if (!location) return;
    setLoading(true);
    Promise.all([loadItems(), loadCounts()]).finally(() => setLoading(false));
  }, [location, loadItems, loadCounts]);

  const handleCountSaved = (itemId, area, qty) => {
    const key = area === 'display' ? 'display_qty' : 'storage_qty';
    setCounts(prev => {
      const existing = prev.find(c => c.item_id === itemId);
      if (existing) return prev.map(c => c.item_id === itemId ? { ...c, [key]: qty } : c);
      return [...prev, { item_id: itemId, display_qty: 0, storage_qty: 0, [key]: qty, submitted_by_name: user.name }];
    });
  };

  const locationLabel = LOCATIONS.find(l => l.key === location)?.label || '';
  const visibleItems = items.filter(i => !i.hidden);
  const remaining = Math.max(0, visibleItems.length - counts.length);

  const handleMarkComplete = async () => {
    if (!window.confirm(`Mark the count complete? This records 0 for the ${remaining} item${remaining === 1 ? '' : 's'} not yet counted at ${locationLabel}.`)) return;
    setCompleting(true);
    try {
      const r = await fetch(`${API}/api/spirits/counts/complete`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location, week_start: weekStart }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || 'Could not mark complete.');
      await loadCounts();
      toast.success('Count marked complete.');
    } catch (e) { toast.error(e.message); }
    finally { setCompleting(false); }
  };

  if (!location) {
    return (
      <div className="min-h-screen bg-gray-900">
        <TopNav />
        <LocationLanding onSelect={setLocation} lastSync={settings?.last_sync_at} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <TopNav>
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-cream font-bold text-base sm:text-xl truncate">{locationLabel} Spirits Inventory</h1>
          <button onClick={() => setLocation(null)}
            className="text-sm text-gray-400 hover:text-white transition flex-shrink-0">← Locations</button>
        </div>
      </TopNav>

      <div className="px-4 sm:px-6 py-5 max-w-screen-xl mx-auto">
        {loading ? (
          <p className="text-gray-500 text-sm text-center py-12">Loading…</p>
        ) : (
          <>
          {remaining > 0 && (
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-xl border border-gray-700 bg-gray-800 px-4 py-3">
              <p className="text-sm text-gray-400">
                <span className="text-white font-medium">{remaining}</span> item{remaining === 1 ? '' : 's'} not yet counted. If everything left is empty, mark the count complete to record them as 0.
              </p>
              <button onClick={handleMarkComplete} disabled={completing}
                className="text-sm font-medium px-4 py-2 rounded-lg text-white whitespace-nowrap disabled:opacity-50 self-start sm:self-auto"
                style={{ backgroundColor: '#F05A28' }}>
                {completing ? 'Marking…' : 'Mark count complete'}
              </button>
            </div>
          )}
          <InventoryTab
            location={location}
            items={items.filter(i => !i.hidden)}
            counts={counts}
            extras={[]}
            pars={[]}
            weekStart={weekStart}
            canUpload={false}
            onCountSaved={handleCountSaved}
            onExtraSaved={() => {}}
          />
          </>
        )}
      </div>
    </div>
  );
}
